import {
  CheckIcon,
  ClockIcon,
  CloseXIcon,
  DeleteIcon,
  IconProps,
  InProgressIcon,
  StateCircleIcon,
  SupportRadioIcon,
  SupportTvIcon,
  WarningTriangleListIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { IoIosRemoveCircleOutline } from 'react-icons/io';

import { ClipReviewStateEnum, TimeKeepingReviewStateEnum } from '@/features/clips/types';
import { CNMCStateEnum } from '@/features/cnmcs/types';
import { ReviewStateEnum } from '@/features/segments/types';
import { I18nextTranslationKeys } from '@/i18n/i18next';
import { EReviewIncidenceType } from '@/types';

export const MOBILE_WIDTH = 994;
export const LAPTOP_WIDTH = 1280;

export const DESKTOP_MIN_WIDTH = 1600;

// Const for tables in:
// - SegmentCard
// - ClipBodyCard
// - BroadcastScheduleProgramBodySegmentCard
export const BREAKPOINT_RESPONSIVE_TABLES = 2000;

export const TABLE_MOBILE_BREAKPOINT = 920;

export const LANGUAGE_KEY = 'userLanguage';

export const KILOBYTES_TO_BYTES = 1024;
export const MEGABYTES_TO_BYTES = 1024 * KILOBYTES_TO_BYTES;
export const GIGABYTES_TO_BYTES = 1024 * MEGABYTES_TO_BYTES;

const REVIEW_STATES_COMMON_CLASSES = 'flex gap-1 items-center py-1 px-2 rounded-sm';
export type TReviewStates<T = ReviewStateEnum> = {
  label: I18nextTranslationKeys;
  value: T;
  class: string;
  color: string;
  icon: (props: IconProps) => JSX.Element;
};

export const REVIEW_STATES: TReviewStates[] = [
  {
    label: 'Sin revisión',
    value: 6,
    class: `bg-accent-disabled-20 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-accent-active-primary-30',
    icon: DeleteIcon,
  },
  {
    label: 'No revisado',
    value: 0,
    class: `bg-feedback-warning-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-warning-20',
    icon: StateCircleIcon,
  },
  {
    label: 'En revisión',
    value: 2,
    class: `bg-extra-40 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-40',
    icon: InProgressIcon,
  },
  {
    label: 'Pendiente',
    value: 1,
    class: `bg-extra-80 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-80',
    icon: ClockIcon,
  },
  {
    label: 'Revisado',
    value: 3,
    class: `bg-extra-70 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-70',
    icon: CheckIcon,
  },
  {
    label: 'Descartado',
    value: 4,
    class: `bg-feedback-error-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-error-30',
    icon: CloseXIcon,
  },
  {
    label: 'Incidencia',
    value: 5,
    class: `bg-feedback-error-20 ${REVIEW_STATES_COMMON_CLASSES} !text-white`,
    color: 'text-feedback-error-20',
    icon: WarningTriangleListIcon,
  },
];

export const CNMC_REVIEW_STATES: TReviewStates<CNMCStateEnum>[] = [
  {
    label: 'Borrador',
    value: CNMCStateEnum.DRAFT,
    class: `bg-extra-80 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-80',
    icon: StateCircleIcon,
  },
  {
    label: 'Procesando',
    value: CNMCStateEnum.PROCESSING,
    class: `bg-extra-40 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-40',
    icon: InProgressIcon,
  },
  {
    label: 'Procesado',
    value: CNMCStateEnum.PROCESSED,
    class: `bg-feedback-success-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-success-20',
    icon: CheckIcon,
  },
  {
    label: 'Error',
    value: CNMCStateEnum.ERROR,
    class: `bg-feedback-error-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-error-30',
    icon: WarningTriangleListIcon,
  },
  {
    label: 'Cancelado',
    value: CNMCStateEnum.CANCELLED,
    class: `bg-feedback-error-20 ${REVIEW_STATES_COMMON_CLASSES} !text-white`,
    color: 'text-feedback-error-20',
    icon: CloseXIcon,
  },
];

export const SEGMENT_REVIEW_STATES: TReviewStates[] = [
  {
    label: 'Sin revisión',
    value: 6,
    class: `bg-feedback-success-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-success-20',
    icon: (props: IconProps) => (
      <IoIosRemoveCircleOutline className={clsx(props.className, 'h-4 w-4')} />
    ),
  },
  {
    label: 'Requiere revisión',
    value: 0,
    class: `bg-feedback-warning-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-warning-20',
    icon: StateCircleIcon,
  },
  {
    label: 'En revisión',
    value: 2,
    class: `bg-extra-40 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-40',
    icon: InProgressIcon,
  },
  {
    label: 'Pendiente',
    value: 1,
    class: `bg-extra-80 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-80',
    icon: ClockIcon,
  },
  {
    label: 'Revisado',
    value: 3,
    class: `bg-extra-70 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-70',
    icon: CheckIcon,
  },
  {
    label: 'Descartado',
    value: 4,
    class: `bg-feedback-error-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-error-30',
    icon: CloseXIcon,
  },
  {
    label: 'Incidencia',
    value: 5,
    class: `bg-feedback-error-20 ${REVIEW_STATES_COMMON_CLASSES} !text-white`,
    color: 'text-feedback-error-20',
    icon: WarningTriangleListIcon,
  },
];

export const TIME_KEEPING_REVIEW_STATES: TReviewStates<TimeKeepingReviewStateEnum>[] = [
  {
    label: 'Sin revisión',
    value: 0,
    class: `bg-feedback-success-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-success-20',
    icon: (props: IconProps) => (
      <IoIosRemoveCircleOutline className={clsx(props.className, 'h-3 w-3')} />
    ),
  },
  {
    label: 'Pendiente',
    value: 1,
    class: `bg-extra-80 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-80',
    icon: ClockIcon,
  },
  {
    label: 'En revisión',
    value: 2,
    class: `bg-extra-40 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-40',
    icon: InProgressIcon,
  },
  {
    label: 'Revisado',
    value: 3,
    class: `bg-extra-70 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-70',
    icon: CheckIcon,
  },
  {
    label: 'Descartado',
    value: 4,
    class: `bg-feedback-error-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-error-30',
    icon: CloseXIcon,
  },
];

export const CLIP_REVIEW_STATES: TReviewStates<ClipReviewStateEnum>[] = [
  {
    label: 'Requiere revisión',
    value: 0,
    class: `bg-feedback-warning-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-warning-20',
    icon: StateCircleIcon,
  },
  {
    label: 'Pendiente',
    value: 1,
    class: `bg-extra-80 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-80',
    icon: ClockIcon,
  },
  {
    label: 'En revisión',
    value: 2,
    class: `bg-extra-40 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-40',
    icon: InProgressIcon,
  },
  {
    label: 'Revisado',
    value: 3,
    class: `bg-extra-70 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-70',
    icon: CheckIcon,
  },
  {
    label: 'Descartado',
    value: 4,
    class: `bg-feedback-error-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-error-30',
    icon: CloseXIcon,
  },
  {
    label: 'Incidencia',
    value: 5,
    class: `bg-feedback-error-20 ${REVIEW_STATES_COMMON_CLASSES} !text-white`,
    color: 'text-feedback-error-20',
    icon: WarningTriangleListIcon,
  },
  {
    label: 'Sin revisión',
    value: 6,
    class: `bg-feedback-success-10 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-feedback-success-20',
    icon: (props: IconProps) => (
      <IoIosRemoveCircleOutline className={clsx(props.className, 'h-4 w-4')} />
    ),
  },
  {
    label: 'No revisado',
    value: 7,
    class: `bg-accent-disabled-20 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-accent-active-primary-30',
    icon: DeleteIcon,
  },
];

export const REVIEW_INCIDENCE_TYPES = [
  {
    label: 'Varios',
    value: EReviewIncidenceType.OTHERS,
  },
  {
    label: 'No emitido',
    value: EReviewIncidenceType.NOT_BROADCAST,
  },
  {
    label: 'No grabado',
    value: EReviewIncidenceType.NOT_RECORDED,
  },
  {
    label: 'Sonido deficiente',
    value: EReviewIncidenceType.poor_sound,
  },
  {
    label: 'Imagen deficiente',
    value: EReviewIncidenceType.poor_image,
  },
  {
    label: 'Sonido e imagen deficiente',
    value: EReviewIncidenceType.poor_sound_and_image,
  },
];

type TChannelTypeState = {
  label: I18nextTranslationKeys;
  value: string;
  class: string;
  color: string;
  icon: (props: IconProps) => JSX.Element;
};
export const CHANNEL_TYPE_STATES: TChannelTypeState[] = [
  {
    label: 'TV',
    value: 'tvts',
    class: `bg-accent-disabled-20 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-10',
    icon: SupportTvIcon,
  },
  {
    label: 'Radio',
    value: 'rts',
    class: `bg-accent-disabled-20 ${REVIEW_STATES_COMMON_CLASSES}`,
    color: 'text-extra-70',
    icon: SupportRadioIcon,
  },
];

export const EPSILON = 0.0001;

export const SELECTED_ROW_OUTLINE_CLASSES =
  'outline outline-[3px] outline-[var(--tw-shadow-color)] outline-offset-[-3px]';

/** Rows still showing the previous result while the one that was asked for is on its way. */
export const STALE_ROWS_CLASSES = 'opacity-50 transition-opacity duration-200';

export const API_MAX_LIMIT = 2000;
